import { lazy, useEffect } from 'react';
import { useRouteError, Outlet, Navigate } from 'react-router-dom';
import { ROUTES } from './shared/constants/routes';
import * as Sentry from '@sentry/react';
import { useAppSelector } from '@/shared/hooks/store';
import { Error } from '@/shared/ui/loading/routeLoading.tsx';
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { unauthorized } from './shared/hocs/unauthorized';
import ConsolidatedRouteHandler from './ConsolidatedRouteHandler.tsx';
import RouteLoading from './shared/ui/loading/routeLoading.tsx';
import OnBoardingLayout from './modules/onboarding/components/layout';
import ConfirmCodePage from './modules/auth/pages/confirm-code/index.tsx';
import SetPassword from './modules/auth/pages/set-password/index.tsx';
import { selectIsAuthorized } from '@/store/slices/auth/selectors.ts';
import { selectUserProfileInfo } from '@/store/slices/onboarding/selectors.ts';
import ThankYou from './modules/thankYou/index.tsx';
/*
    ! Note: Please use component imports via lazy
*/

const Login = lazy(() => import('./modules/auth/auth.ts').then(x => ({ default: x.Login })));
const RegisterSwitch = lazy(() => import('./modules/auth/auth.ts').then(x => ({ default: x.RegisterSwitch })));
const Onboarding = lazy(() => import('./modules/onboarding/onboarding.tsx'));
const Dashboard = lazy(() => import('./modules/dashboard/dashboard.tsx'));

const isProd = import.meta.env.VITE_APP_IS_PRODUCTION === 'true';

let sentryCreateBrowserRouter = createBrowserRouter;
if (isProd) {
    Sentry.init({
        release: 'v1.0.0', // temporarily hardcoded
        dsn: 'https://5c951421715c126568000870f8a25f31@o4507772120072192.ingest.us.sentry.io/4507772189474816',
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes
            })
        ],
        tracesSampleRate: 1.0,
        ignoreErrors: ["CanceledError"],
    });
    sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);
}

const UnauthedWrappedLogin = unauthorized(() => (
    <RouteLoading>
        <OnBoardingLayout spacings={false}>
            <Login />
        </OnBoardingLayout>
    </RouteLoading>
));

const UnauthedWrappepSetPassword = unauthorized(() => (
    <RouteLoading>
        <OnBoardingLayout spacings={false}>
            <SetPassword />
        </OnBoardingLayout>
    </RouteLoading>
));

const UnauthedWrapperChatLink = () => {
    useEffect(() => {
        sessionStorage.setItem('chatAutoOpen', 'true');
    }, []);

    return <Navigate to="/" />;
};

const UnauthedConfirmCode = unauthorized(() => (
    <RouteLoading>
        <OnBoardingLayout spacings={false} hasFooter={false}>
            <ConfirmCodePage />
        </OnBoardingLayout>
    </RouteLoading>
));

const UnauthedWrappedRegisterV2 = unauthorized(() => (
    <RouteLoading>
        <RegisterSwitch />
    </RouteLoading>
));

export function CustomRouteErrorBoundaryWithSentryReporter() {
    const error = useRouteError() as Error;
    useEffect(() => {
        if (error) {
            Sentry.captureException(error);
        }
    }, [error]);

    return <Error />;
}

const TrackedLayout = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const isAuthorized = useAppSelector(selectIsAuthorized);
    const userProfileInfo = useAppSelector(selectUserProfileInfo);

    useEffect(() => {
        if ((userProfileInfo?.mrn || []).length > 0) {
            Sentry.setUser({
                id: userProfileInfo?.mrn[0]
            });
        }
    }, [userProfileInfo]);

    useEffect(() => {
        if (!isAuthorized && (location.pathname.includes(ROUTES.DASHBOARD.MAIN) || location.pathname.includes(ROUTES.ONBOARDING.MAIN))) {
            sessionStorage.setItem("redirectAfterLogin", location.pathname);
        }
    }, [])

    // on location reference change send a pageview event to posthog
    useEffect(() => {
        if (window.posthog && window?.location?.href) {
            window.posthog.capture("$pageview", {
                "$current_url": window.location.href,
            });
        }
    }, [location]);

    return <>{children}</>;
};

export const AppRouter = sentryCreateBrowserRouter([
    {
        element: <TrackedLayout><Outlet /></TrackedLayout>,
        errorElement: <CustomRouteErrorBoundaryWithSentryReporter />,
        children: [
            {
                path: ROUTES.LOGIN,
                element: <UnauthedWrappedLogin />
            },
            {
                path: ROUTES.SET_PASSWORD,
                element: <UnauthedWrappepSetPassword />
            },
            {
                path: ROUTES.SIGN_UP,
                element: <UnauthedWrappedRegisterV2 />
            },
            {
                path: ROUTES.CONFIRM_CODE,
                element: <UnauthedConfirmCode />
            },
            {
                path: ROUTES.THANK_YOU,
                element: (
                    <RouteLoading>
                        <ConsolidatedRouteHandler Component={ThankYou} thankYou />
                    </RouteLoading>
                ),
            },
            {
                path: `${ROUTES.ONBOARDING.MAIN}/*`,
                element: (
                    <RouteLoading>
                        <ConsolidatedRouteHandler Component={Onboarding} onboarding />
                    </RouteLoading>
                )
            },
            {
                path: `${ROUTES.DASHBOARD.MAIN}/*`,
                element: (
                    <RouteLoading>
                        <ConsolidatedRouteHandler Component={Dashboard} />
                    </RouteLoading>
                )
            },
            {
                path: ROUTES.CHAT,
                element: <UnauthedWrapperChatLink />
            },
            {
                path: '*',
                element: (
                    <RouteLoading>
                        <ConsolidatedRouteHandler defaultPath />
                    </RouteLoading>
                )
            }
        ]
    }
]);


export default AppRouter;
